import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { BehaviorSubject, merge, Observable, Subject, Subscription } from 'rxjs';
import { bufferTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { slashCommands } from 'common/common/constants/slash-commands';
import { bcEnums, TransferType } from 'common/common/constants/boldchat-enums';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { LinkTargetBlankFilter } from 'common/html-helper/link-target-blank';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { EditorMessages } from 'shared/components/editor-messages/editor-messages';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { Chat } from 'common/xislivechat/models/chat';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { ActionPermissionType } from 'common/common/constants/permission-type-enum';
import { WrapupPanelService } from '../../shared/components/wrapup-panel/wrapup-panel.service';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { LaneManagerService } from 'shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { ChatMessageGroupModel } from 'shared/components/chat-messages/chat-message-group.model';
import { NanorepService } from 'shared/components/nanorep-panel/nanorep.service';
import { WrapUp } from 'common/api/models/wrap-up.model';
import { BcAuth } from 'common/auth/auth.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { BcCannedMessagesRepo } from 'common/canned-messages/canned-messages-repo';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcRestApi } from 'common/api/rest-api';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { TinyMCEEditorComponent } from 'shared/components/editor/editor.component';
import { ChatMessagesQueueService } from 'common/chat-messages-queue/chat-messages-queue.service';
import { WorkItemLaneType } from 'shared/components/lane-buttons/shared/services/lane-manager/work-item-lane-type.enum';
import { MessageGroupService } from 'shared/services/message-group/message-group.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { DiscussionsService } from 'shared/components/discussions/discussions.service';
import { ChatsService } from 'common/chats/chats.service';
import { ClosingConfirmationService } from 'shared/components/confirmation-modal/confirmation-modal.service';
import { ConfirmationModalComponent } from 'shared/components/confirmation-modal/confirmation-modal.component';
import { ChatButtonsComponent } from './chat-buttons/chat-buttons.component';
import { ChatMoreActionsComponent } from 'shared/components/work-widget-control-panel/chat-more-actions/chat-more-actions.component';
import { ConfirmationModalConfig } from 'shared/components/confirmation-modal/confirmation-modal-config';
import { ChatMessagesService } from 'common/chat-messages/chat-messages.service';
import { AssignmentInfo } from 'components/work-item-lane-list/work-item-lane/work-item-widget/chat-widget/chat-box/assignment-info.model';
import { BcConfigService } from 'common/config/provider';
import { ChatMessagesStoreFactory } from 'common/chat-messages-store-factory/chat-messages-store-factory.service';
import { InnerTextPipe } from 'shared/components/canned-message-panel/filters/inner-text.pipe';
import { InnerHtmlPipe } from 'shared/components/canned-message-panel/filters/inner-html.pipe';
import { RemoteControlService } from 'shared/services/remote-control/remote-control.service';
import { WorkItemObservableCacheService } from '../../shared/services/work-item-observable-cache.service';

export const MESSAGE_UPDATE_BUFFER_DURATION = 300;

@Component({
  selector: 'app-chat-box',
  templateUrl: './chat-box.component.html',
  styleUrls: ['./chat-box.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatBoxComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input()
  cannedMessageRequest: CannedMessageRequest;

  @Input()
  isCannedMessagePanelOpen: boolean;

  @Input()
  isNanorepVisible: boolean;

  @Input()
  workItem: ChatItem;

  @Input()
  chat: Chat;

  @Input()
  isDiscussionPanelOpened: boolean;

  @Input()
  hideWrapUpPanel: boolean;

  @Input()
  canJoinChat: boolean = false;

  @ViewChild('editorComponent')
  editorComponent: TinyMCEEditorComponent;

  @ViewChild('endConfirmationModal')
  confirmationModalComponent: ConfirmationModalComponent;

  @ViewChild('blockConfirmationModal')
  blockConfirmationModalComponent: ConfirmationModalComponent;

  @ViewChild('videoAutoStartConfirmationModal')
  videoAutoStartConfirmationModal: ConfirmationModalComponent;

  @ViewChild('videoWithAcdConfirmationModal')
  videoWithAcdConfirmationModal: ConfirmationModalComponent;

  @ViewChild('videoConfirmationModal')
  videoConfirmationModal: ConfirmationModalComponent;

  @ViewChild('cancelRemoteControlSessionConfirmationModal')
  cancelRemoteControlSessionConfirmationModal: ConfirmationModalComponent;

  @ViewChild(ChatButtonsComponent)
  chatButtonsComponent: ChatButtonsComponent;

  @ViewChild(ChatMoreActionsComponent)
  chatMoreActionsComponent: ChatMoreActionsComponent;

  moment;
  KeyboardKeys;
  linkTargetBlankFilter;
  hasSmartResponseFeature;
  operator;
  keyDownEventListener;
  scrollToBottomTimeout;
  isAttached;
  editorCommandList;
  isTyping;
  typingTimeout;
  messages: ChatMessageGroupModel[];
  visitor;
  message;
  initialMessage;
  editorMessages;
  editorDisabled;
  isTransferPanelActive;
  isSmartResponsesActive;
  isWrapupPanelActive;
  isMoreActionsVisible;

  chatAssignmentInfo: AssignmentInfo;

  transferPanelScope;
  scrollPane;
  editorInstance;

  endChatTimeout;
  unbindActiveChatWatcher;

  bcEnums;
  welcomeCannedMessagePromise;
  wrapups: WrapUp[][] = [];
  areWrapupsLoaded = false;

  isNanorepEnabled = false;
  welcomeMessage: string;
  hasDiscussionsFeature = false;
  hasBlockChatPermission = false;
  hasFlagPermission = false;
  editorInitSetting;
  formattedAssignmentDate: string;
  transferType: TransferType;

  confirmationVisible = false;
  blockConfirmationVisible = false;
  videoAutoStartConfirmationModalVisible = false;
  videoWithAcdConfirmationModalVisible = false;
  videoConfirmationModalVisible = false;
  cancelRemoteControlSessionConfirmationModalVisible = false;
  confirmationBoxConfig: ConfirmationModalConfig = {
    title: 'bcChatBox.endChatConfirmation.title',
    subtitle: 'bcChatBox.endChatConfirmation.subtitle',
    buttonTitle: 'bcChatBox.endChatConfirmation.yes',
    secondaryActionTitle: 'bcChatBox.endChatConfirmation.endChatDontShowAgain',
    cancelTitle: 'bcChatBox.endChatConfirmation.continue'
  };
  blockConfirmationBoxConfig: ConfirmationModalConfig = {
    title: 'bcChatBox.endChatConfirmation.blockTitle',
    subtitle: 'bcChatBox.endChatConfirmation.blockSubtitle',
    buttonTitle: 'bcChatBox.endChatConfirmation.blockIp',
    cancelTitle: 'bcChatBox.endChatConfirmation.continue'
  };
  videoAutoStartConfirmationConfig: ConfirmationModalConfig = {
    title: 'bcChatBox.videoAutoStartConfirmation.title',
    subtitle: 'bcChatBox.videoAutoStartConfirmation.subtitle',
    buttonTitle: 'bcChatBox.videoAutoStartConfirmation.confirm',
    secondaryActionTitle: 'bcChatBox.videoAutoStartConfirmation.cancel'
  };
  videoWithAcdConfirmationConfig: ConfirmationModalConfig = {
    title: 'bcChatBox.videoWithAcdConfirmation.title',
    subtitle: 'bcChatBox.videoWithAcdConfirmation.subtitle',
    buttonTitle: 'bcChatBox.videoWithAcdConfirmation.confirm',
    secondaryActionTitle: 'bcChatBox.videoWithAcdConfirmation.cancel'
  };
  videoConfirmationConfig: ConfirmationModalConfig = {
    title: 'bcChatBox.videoConfirmation.title',
    subtitle: 'bcChatBox.videoConfirmation.subtitle',
    buttonTitle: 'bcChatBox.videoConfirmation.confirm',
    secondaryActionTitle: 'bcChatBox.videoConfirmation.cancel'
  };
  cancelRemoteControlSessionConfirmationConfig: ConfirmationModalConfig = {
    title: 'bcChatBox.remoteControlSessionCancelConfirmation.title',
    subtitle: 'bcChatBox.remoteControlSessionCancelConfirmation.subtitle',
    buttonTitle: 'bcChatBox.remoteControlSessionCancelConfirmation.confirm',
    secondaryActionTitle: 'bcChatBox.remoteControlSessionCancelConfirmation.cancel'
  };

  private _chatMessagesStoreSubscription: Subscription;
  private _setFocusOnEditorSubscription: Subscription;
  private _toggleSmartResponsesPanelSubscription: Subscription;
  private _sendCannedMessageToEditorSubscription: Subscription;
  private _sendSmartResponseToEditorSubscription: Subscription;
  private _videoConfirmationVisibilityChangedSubscription: Subscription;
  private _videoAutoStartConfirmationVisibilityChangedSubscription: Subscription;
  private _videoWithAcdConfirmationVisibilityChanged: Subscription;
  private _cancelRemoteControlSessionConfirmationVisibilityChanged: Subscription;
  private _onDestroySubject = new Subject<void>();
  private _oldValue;
  private _isSmartAdvisor: boolean;
  private _cannedMessageID: string;

  private static readonly DomParser = new DOMParser();
  private static readonly InnerHtmlPipe = new InnerHtmlPipe();
  private readonly isFileTransferForOperatorEnabledSubject = new BehaviorSubject<boolean>(false);

  constructor(
    readonly innerTextPipe: InnerTextPipe,
    private readonly _bcAnalytics: BcAnalytics,
    private readonly _bcAuth: BcAuth,
    private readonly _bcConfig: BcConfigService,
    private readonly _bcCannedMessagesRepo: BcCannedMessagesRepo,
    private readonly _bcSettingsService: BcSettingsService,
    private readonly _bcRestApi: BcRestApi,
    private readonly _chatMessages: ChatMessagesService,
    private readonly _chatMessagesStore: ChatMessagesStoreFactory,
    private readonly _chats: ChatsService,
    private readonly _userPermissionService: UserPermissionService,
    private readonly _wrapupPanelService: WrapupPanelService,
    private readonly _workItemStateHandler: WorkItemStateHandler,
    private readonly _translateService: TranslateService,
    private readonly _workAreaManager: WorkAreaManager,
    private readonly _rxEmitter: RxEmitter,
    private readonly _element: ElementRef,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _chatMessagesQueueService: ChatMessagesQueueService,
    private readonly _messageGroupService: MessageGroupService,
    private readonly _laneManager: LaneManagerService,
    private readonly _discussionsService: DiscussionsService,
    private readonly _closingConfirmationService: ClosingConfirmationService,
    private readonly _nanorepService: NanorepService,
    private readonly _ngZone: NgZone,
    private readonly _remoteControlService: RemoteControlService,
    private readonly _workItemObservableCacheService: WorkItemObservableCacheService
  ) {
    this.moment = moment;
    this.KeyboardKeys = KeyboardKeyCodes;
    this.linkTargetBlankFilter = LinkTargetBlankFilter();
    this.hasSmartResponseFeature = false;
    this.bcEnums = bcEnums;

    this.operator = this._bcAuth.getSession();

    this.resetState();

    this.initCommandList();

    this.editorInitSetting = { toolbar: 'bold italic underline link pushLink insertImage removeformat fileUpload' };
  }

  get isFileTransferForOperatorEnabled$(): Observable<boolean> {
    return this.isFileTransferForOperatorEnabledSubject.asObservable();
  }

  onEditorSubmit(editorContent): void {
    let message = this.preProcessMessage(editorContent);
    if (this.checkSlashCommand()) {
      message = '';
    }

    if (this.isMessageNotEmpty(message)) {
      this.sendTyping(false);
      this.cancelIsTypingTimeout();

      const messageObj = {
        ChatID: this.chat.ChatID,
        // TODO Operator name is buggy on the end point: it does not use this value, but it should
        Name: 'Operator',
        Text: message,
        CannedMessageID: this._cannedMessageID
      };

      this._chatMessagesQueueService.createSubscriptionForChat(this.chat.ChatID);
      this._chatMessages.addMessageToQueue(this.chat.ChatID, messageObj);
      this.clearEditor();
    }
  }

  onEditorSetupDone(editor): void {
    this.editorInstance = editor;

    this.editorInstance.on('keyUp', this.handleTypingOnKeyUp.bind(this));

    this.editorInstance.on('init', () => {
      if (this.isFocused()) {
        this.setFocusOnMainElement();
      }

      this.setWelcomeMessageIfNotAnsweredAndEditorIsEmpty();
      this._changeDetector.detectChanges();
      this.scrollToBottom();
    });

    this.editorInstance.settings.placeholder = this._translateService.instant('bcChatBox.editorPlaceholder');
  }

  handleTypingOnKeyUp(): void {
    if (!this.isTyping && this.editorInstance.getContent() !== '') {
      this.sendTyping(true);
      this.startIsTypingTimeout();
    } else if (this.isTyping) {
      this.cancelIsTypingTimeout();
      this.startIsTypingTimeout();
    }
  }

  ngOnChanges(): void {
    this.scrollPane = this._element.nativeElement.querySelector('.scroll-bottom-list');
    let newValue;
    if (this.chat) {
      newValue = this.chat.isConversationActive(this.operator);
      if (newValue !== this._oldValue && newValue === false) {
        if (!this.chat.isClosed() && this.chat.isEnded()) {
          this.isWrapupPanelActive = true;
        }
      }
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.chat) {
      this._oldValue = this.chat.isConversationActive(this.operator);
    }

    this.init();
    if (!this.hideWrapUpPanel) {
      this.loadWrapups();
    }
    this._collectFeatureValues().then(() => this._getChatWindowSettings());

    this.hasBlockChatPermission = this._hasBlockChatPermission();
    this.hasFlagPermission = this._hasFlagPermission();

    this._setFocusOnEditorSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.SetDefaultFocusInLane}${this.workItem.id}`,
      () => {
        this.setFocusOnMainElement();
      }
    );

    const onExpandedModeChanged$ = this._rxEmitter.getNamespaceObservable(MessageBusEvents.ToggleExpandedMode);

    const onActiveAssistStatusChanged$ = this._workItemObservableCacheService
      .getWorkItemObservable(this.workItem.id, this.workItem.type).pipe(
        map((chat: ChatItem) => chat.ActiveAssistStatus),
        distinctUntilChanged()
      );

    merge(onExpandedModeChanged$, onActiveAssistStatusChanged$).pipe(
      takeUntil(this._onDestroySubject)
    ).subscribe(() => {
      setTimeout(() => {
        this.isAttached = true;
        this.scrollToBottom();
      }, 1000);
    });

    this._toggleSmartResponsesPanelSubscription = this._rxEmitter
      .getNamespaceObservable(
        LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.ToggleSmartResponses, this.workItem.id)
      )
      .subscribe(() => {
        this.toggleSmartResponsePanel();
      });

    this._sendCannedMessageToEditorSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.SendCannedMessageToEditor}${this.workItem.id}`,
      (data) => {
        this._setLastSelectedCannedMessageIdForSendMessage(data);
      }
    );

    this._sendSmartResponseToEditorSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.SendSmartResponseToEditor}${this.workItem.id}`,
      (data) => {
        this._setLastSelectedCannedMessageIdForSendMessage(data);
      }
    );

    this._videoConfirmationVisibilityChangedSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.VideoConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.setVideoConfirmationVisibility(isVisible);
      }
    );

    this._videoAutoStartConfirmationVisibilityChangedSubscription = this._rxEmitter.subscribe(
      `${MessageBusEvents.VideoAutoStartConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.setVideoAutoStartConfirmationVisibility(isVisible);
      }
    );

    this._videoWithAcdConfirmationVisibilityChanged = this._rxEmitter.subscribe(
      `${MessageBusEvents.VideoWithAcdConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.setVideoWithAcdConfirmationVisibility(isVisible);
      }
    );

    this._cancelRemoteControlSessionConfirmationVisibilityChanged = this._rxEmitter.subscribe(
      `${MessageBusEvents.CancelRemoteControlSessionConfirmationVisibilityChanged}${this.workItem.id}`,
      (isVisible: boolean) => {
        this.setCancelRemoteControlSessionConfirmationVisibility(isVisible);
      }
    );

    try {
      this.chatAssignmentInfo = await this._bcRestApi.getChatAssignmentInfo(this.chat.ChatID);
      if (this.chatAssignmentInfo) {
        this.formattedAssignmentDate = this.moment.utc(this.chatAssignmentInfo.assignedAt).local().format('llll');
        if (this.chatAssignmentInfo.assignedFrom === 'Bot') {
          this.transferType = TransferType.ESCALATED;
        } else if (this.chatAssignmentInfo.assignmentType === 'OPERATOR') {
          this.transferType = TransferType.TRANSFERRED;
        } else {
          this.transferType = TransferType.AUTO_TRANSFERRED;
        }
      }
    } catch (err) {
      // Ignore, keep chatAssignmentInfo null
    }
  }

  private _setLastSelectedCannedMessageIdForSendMessage(data): void {
    if (data && (data.id || data.cannedMessageId)) {
      this._cannedMessageID = data.id || data.cannedMessageId;
    }
  }

  private _safeUnsubscribe(sub: Subscription): void {
    if (sub) {
      sub.unsubscribe();
      sub = null;
    }
  }

  ngOnDestroy(): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    if (this.endChatTimeout) {
      clearTimeout(this.endChatTimeout);
    }

    if (this.unbindActiveChatWatcher) {
      this.unbindActiveChatWatcher();
    }

    this.cancelScrollToBottomTimeout();

    this._safeUnsubscribe(this._chatMessagesStoreSubscription);
    this._safeUnsubscribe(this._setFocusOnEditorSubscription);
    this._safeUnsubscribe(this._toggleSmartResponsesPanelSubscription);
    this._safeUnsubscribe(this._sendCannedMessageToEditorSubscription);
    this._safeUnsubscribe(this._sendSmartResponseToEditorSubscription);
    this._safeUnsubscribe(this._videoConfirmationVisibilityChangedSubscription);
    this._safeUnsubscribe(this._videoAutoStartConfirmationVisibilityChangedSubscription);
    this._safeUnsubscribe(this._videoWithAcdConfirmationVisibilityChanged);
    this._safeUnsubscribe(this._cancelRemoteControlSessionConfirmationVisibilityChanged);

    this._onDestroySubject.next();
    this._onDestroySubject.complete();

    this._element.nativeElement.removeEventListener('keydown', this.keyDownEventListener);
  }

  ngAfterViewInit(): void {
    this.keyDownEventListener = (e) => this.handleKeyDown(e);
    this._element.nativeElement.addEventListener('keydown', this.keyDownEventListener);

    this._changeDetector.detectChanges();
    this.isAttached = true;
    this.scrollToBottom();
  }

  init(): void {
    const chatId = this.chat.ChatID;

    if (!this.isWrapupPanelActive && this.chat.isEnded()) {
      this.isWrapupPanelActive = true;
    }

    this._chatMessagesStoreSubscription = this._chatMessagesStore
      .create(chatId)
      .pipe(bufferTime(MESSAGE_UPDATE_BUFFER_DURATION))
      .subscribe((messagesBuffer) => {
        this.setMessages(messagesBuffer);
        this.scrollToBottomIfMessageReceived(messagesBuffer);
      });
  }

  initCommandList(): void {
    this.editorCommandList = [
      { shortcut: slashCommands.CANNED_MESSAGES, description: 'bcChatBox.commands.cannedMessage' },
      { shortcut: slashCommands.END_CHAT, description: 'bcChatBox.commands.endChat' },
      { shortcut: slashCommands.ASSIGN, description: 'bcChatBox.commands.reassign' }
    ];
  }

  onVisitorHistoryLoaded(): void {
    this.scrollPane = this._element.nativeElement.querySelector('.scroll-bottom-list');
    this.isAttached = true;
    this.scrollToBottom();
  }

  resetState(): void {
    if (this.isTyping) {
      this.sendTyping(false);
    }
    this.cancelIsTypingTimeout();
    this.isTyping = false;
    this.typingTimeout = null;

    this.messages = [];
    this.visitor = {};

    this.message = {
      text: ''
    };

    this.initialMessage = '';
    this.editorMessages = new EditorMessages();

    this.editorDisabled = false;
    this.isTransferPanelActive = false;
    this.isSmartResponsesActive = false;
    this.isWrapupPanelActive = false;
    this.isMoreActionsVisible = false;
  }

  openDiscussionPanel(): void {
    this._laneManager.openLane(this.workItem.id, WorkItemLaneType.Discussions.toString(), this.workItem.type);
  }

  setFocusOnMainElement(): void {
    // TODO: this is unmaintainable due to the number of cases - refactor this according to this ticket: https://jira.3amlabs.net/browse/BOLD-22884
    if (this.editorInstance && !this.editorDisabled && !this.isMoreActionsVisible) {
      if (this.editorInstance.initialized) {
        this._rxEmitter.send(broadcastEvents.focusEditor, null);
      } else {
        this.editorInstance.on('init', () => {
          this._rxEmitter.send(broadcastEvents.focusEditor, null);
        });
      }
    } else {
      this._element.nativeElement.focus();
    }
  }

  isFocused(): boolean {
    return this._workItemStateHandler.isFocused(this.workItem.id);
  }

  setWelcomeMessageIfNotAnsweredAndEditorIsEmpty(): void {
    if (this.welcomeMessage) {
      setWelcomeMessage.call(this, this.welcomeMessage);

      return;
    }

    if (this.welcomeCannedMessagePromise) {
      return;
    }
    this.welcomeCannedMessagePromise = this._bcCannedMessagesRepo
      .getChatWelcomeCannedMessage(
        this.chat.ChatWindowDefID,
        this.chat.ChatID,
        this.chat.LanguageCode,
        this.chat.WebsiteDefID
      )
      .then((welcomeCannedMessage) => {
        if (!welcomeCannedMessage) {
          return;
        }
        setWelcomeMessage.call(this, welcomeCannedMessage);
      });

    function setWelcomeMessage(welcomeMessage) {
      setTimeout(() => {
        function areMessagesEqual(a, b) {
          if (!a && !b) {
            return true;
          }

          if (!a || !b) {
            return false;
          }

          const normalizedA = this.innerTextPipe.transform(a).replace(/\n/g, '');
          const normalizedB = this.innerTextPipe.transform(b).replace(/\n/g, '');

          return normalizedA === normalizedB;
        }

        const welcomeMessageIsInserted = areMessagesEqual.call(
          this,
          this.editorInstance.getContent(),
          welcomeMessage.text
        );
        this.welcomeMessage = welcomeMessage;

        if (!welcomeMessage) {
          return;
        }

        if (this.message.text && !welcomeMessageIsInserted) {
          return;
        }

        if (this.messages.some((message) => message.type === bcEnums.messageTypes.OPERATOR)) {
          if (welcomeMessageIsInserted) {
            this.message.text = '';
          }

          return;
        }

        if (!this.welcomeMessageWasSetOnce && !welcomeMessageIsInserted) {
          this.welcomeMessageWasSetOnce = true;

          this.addCannedMessage(welcomeMessage);
        }
        this.selectTextInEditor();
      }, 500);
    }
  }

  selectTextInEditor(): void {
    if (!this.editorInstance) {
      return;
    }

    setTimeout(() => {
      if (this.editorInstance.selection) {
        this.editorInstance.selection.select(this.editorInstance.getBody(), true);
      }
    });
  }

  isAnswerable(): boolean {
    return this.chat && this.chat.isAnswerable(this.operator);
  }

  isDisconnected(): boolean {
    return this._bcConfig.current.features.visitorConnectivityMonitoring && this.chat && this.chat.isDisconnected();
  }

  isChatEnded(): boolean {
    return this.chat && this.chat.isEnded();
  }

  isChatActive(): boolean {
    return this.chat && this.chat.isAnswered() && this.chat.isAlive();
  }

  isEndable(): boolean {
    return this.chat && this.chat.isEndable();
  }

  showConfirmation(): void {
    this.confirmationVisible = true;
  }

  closeConfirmation(): void {
    this.confirmationVisible = false;
  }

  endChatAndDontShowModal(): void {
    this._closingConfirmationService.setClosingConfirmation(this.workItem.type, true);
    this._rxEmitter.send(MessageBusEvents.ShowConfirmationPanelTooltip, null);
    this.endChat();
  }

  endOrShowConfirmation(): void {
    if (this.isConfirmationModalDisabled()) {
      this.endChat();
    } else {
      this.showConfirmation();
    }
  }

  endChat(): void {
    if (this.chat) {
      this.closeConfirmation();
      this._chats.endChat(this.chat.ChatID).then(() => {
        this.endChatTimeout = setTimeout(() => {
          if (!this.chat.isClosed() && !this.isWrapupPanelActive) {
            this.toggleWrapupPanel();
          }
        }, 500);
      });
    }
  }

  isConfirmationModalDisabled(): boolean {
    return <boolean>this._closingConfirmationService.getClosingConfirmationSetting(this.workItem.type);
  }

  openTransferChatSettings(): void {
    this.isTransferPanelActive = true;
    this._bcAnalytics.eventTrack('ChatTransferPanel', { action: bcEnums.panelAction.OPEN });
  }

  closeTransferChatSettings(): void {
    if (this.isTransferPanelActive) {
      this._bcAnalytics.eventTrack('ChatTransferPanel', { action: bcEnums.panelAction.CLOSE });
      this.isTransferPanelActive = false;
      this.transferPanelScope = null;
      this._changeDetector.detectChanges();
      this.scrollPane = this._element.nativeElement.querySelector('.scroll-bottom-list');
      this.isAttached = true;
      this.scrollToBottom();
      this.setFocusOnEditor();
    }
  }

  answerChat(): Promise<void> {
    if (this.chat && this.isAnswerable()) {
      return this._chats.answerChat(this.chat).then(() => {
        this.setWelcomeMessageIfNotAnsweredAndEditorIsEmpty();
      });
    }
  }

  setMessages(messagesBuffer): void {
    if (!messagesBuffer.length) {
      return;
    }
    this.getScrollPane();
    this.setIsAttachedToBottom();
    this.messages = this._messageGroupService.addNewMessagesToGroups(
      this.messages,
      messagesBuffer[messagesBuffer.length - 1],
      WorkItemTypes.CHAT
    );
    this._rxEmitter.send(
      LaneManagerService.generateWorkItemObservableNamespace(MessageBusEvents.SendWorkItemContent, this.workItem.id),
      this.messages
    );
    this._changeDetector.detectChanges();
  }

  getScrollPane(): any {
    if (!this.scrollPane) {
      this.scrollPane = this._element.nativeElement.querySelector('.scroll-bottom-list');
    }
    return this.scrollPane;
  }

  setIsAttachedToBottom(): void {
    if (!this.scrollPane) {
      this.isAttached = true;
    } else {
      this.isAttached = this.scrollPane.scrollTop + this.scrollPane.clientHeight + 1 >= this.scrollPane.scrollHeight;
    }
  }

  scrollToBottomIfMessageReceived(messagesBuffer): void {
    if (!messagesBuffer.length) {
      return;
    }
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    if (this.scrollPane && this.isAttached) {
      this.scrollPane.scrollTop = this.scrollPane.scrollHeight;
    }
  }

  isMessageNotEmpty(message): boolean {
    const replacedMessage = message.replace(/<(?:.|\n)*?>/gm, '').trim();
    const doc = ChatBoxComponent.DomParser.parseFromString(message, 'text/html');
    const imageElement: HTMLImageElement = doc.querySelector('img');
    return !!imageElement || replacedMessage !== '';
  }

  changePushLinkNodes(message): any {
    return ChatBoxComponent.InnerHtmlPipe.transform(message).replace(/\[(https?:\/\/[^\]]*)\]/g, '<push>$1</push>');
  }

  addTargetToAnchors(message): any {
    if (!message) {
      return message;
    }

    return this.linkTargetBlankFilter(message);
  }

  preProcessMessage(message): any {
    const anchorTargetBlankAdded = this.addTargetToAnchors(message);
    const cleanMarkupEnrichedWithPushLinks = this.changePushLinkNodes(anchorTargetBlankAdded);
    return cleanMarkupEnrichedWithPushLinks;
  }

  checkSlashCommand(): boolean {
    const message = this.editorInstance.getContent({ format: 'text' });
    if (message.startsWith('/')) {
      this.executeCommand(message);
      return true;
    }
    return false;
  }

  clearEditor(): void {
    this.editorInstance.setContent('');
    this.message.text = '';
    this._cannedMessageID = undefined;
  }

  executeCommand(command): void {
    this._bcAnalytics.eventTrack('SlashCommand', { type: 'chat', command });
    switch (command) {
      case slashCommands.CANNED_MESSAGES:
        this.openCannedMessagePanel(false);
        break;
      case slashCommands.END_CHAT:
        this.endChat();
        break;
      case slashCommands.ASSIGN:
        if (this.workItem.hasActiveAssistedSession) {
          this.editorMessages.addEditorMessage('transferDisabled', 'bcChatBox.activeRcTransferDisabled');
        } else {
          this.openTransferChatSettings();
        }
        break;
      case slashCommands.WRAP_UP:
        if (this.hasWrapUps()) {
          this.isWrapupPanelActive = true;
        } else {
          this.editorMessages.addEditorMessage('invalidCommand', 'bcChatBox.invalidCommand', {
            parsedCommand: command
          });
        }
        break;
      default:
        this._bcAnalytics.eventTrack('InvalidCommand', { type: 'chat', command });
        this.editorMessages.addEditorMessage('invalidCommand', 'bcChatBox.invalidCommand', { parsedCommand: command });
    }
    this.clearEditor();
  }

  handleKeyDown(event): void {
    if (event.which === this.KeyboardKeys.ESC) {
      event.preventDefault();
      event.stopPropagation();

      this.closeCannedMessagePanel();
      this.closeTransferChatSettings();
      this.isWrapupPanelActive = false;
      setTimeout(() => {
        this.setFocusOnMainElement();
      });
    } else if ((event.metaKey || event.ctrlKey) && event.which === this.KeyboardKeys.SPACE) {
      event.preventDefault();
      event.stopPropagation();

      this.toggleSmartResponsePanel();
    }
  }

  startIsTypingTimeout(): void {
    this._ngZone.runOutsideAngular(() => {
      this.typingTimeout = setTimeout(
        () => {
          this.sendTyping(false);
        },
        3000,
        false
      );
    });
  }

  cancelIsTypingTimeout(): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
  }

  cancelScrollToBottomTimeout(): void {
    if (this.scrollToBottomTimeout) {
      clearTimeout(this.scrollToBottomTimeout);
      this.scrollToBottomTimeout = null;
    }
  }

  sendTyping(typing): void {
    if (this.chat) {
      this._bcRestApi.setTypingChat(this.chat.ChatID, typing);
      this.isTyping = typing;
    }
  }

  clearEditorMessage(): void {
    this.editorMessages.clearAllMessages();

    if (this.editorDisabled) {
      this.editorDisabled = false;
    }
  }

  addCannedMessage(cannedMessage): void {
    if (this.editorInstance) {
      this.editorInstance.insertContent(cannedMessage.text);
      this._cannedMessageID = cannedMessage.id;
    }
  }

  toggleCannedMessagePanel(isSmartAdvisor: boolean): void {
    if (this.isCannedMessagePanelOpen && this._isSmartAdvisor === isSmartAdvisor) {
      this.closeCannedMessagePanel();
    } else {
      this.openCannedMessagePanel(isSmartAdvisor);
    }
    this._isSmartAdvisor = isSmartAdvisor;
  }

  openCannedMessagePanel(isSmartAdvisor: boolean): void {
    this._rxEmitter.send(`${MessageBusEvents.ActivateCannedMessagePanel}${this.workItem.id}`, { isSmartAdvisor });
  }

  closeCannedMessagePanel(): void {
    this._rxEmitter.send(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, null);
  }

  toggleWrapupPanel(): void {
    this.isWrapupPanelActive = !this.isWrapupPanelActive;
    this._bcAnalytics.eventTrack('ChatWrapupPanel', {
      action: this.isWrapupPanelActive ? bcEnums.panelAction.OPEN : bcEnums.panelAction.CLOSE
    });
    setTimeout(() => {
      if (!this.isWrapupPanelActive) {
        this.setFocusOnEditor();
      }
    });
  }

  toggleSmartResponsePanel(): void {
    this.initialMessage = this.message.text;
    this.isSmartResponsesActive = !this.isSmartResponsesActive;
    setTimeout(() => {
      if (this.isSmartResponsesActive) {
        this._rxEmitter.send(broadcastEvents.focusSmartResponses, null);
      } else {
        this.setFocusOnEditor();
      }
    });
  }

  closeSmartResponsePanel(): void {
    this.isSmartResponsesActive = false;
  }

  smartResponseSelected(smartResponse): void {
    if (smartResponse) {
      this._rxEmitter.send(`${MessageBusEvents.SendSmartResponseToEditor}${this.workItem.id}`, smartResponse);
    }
    this.setFocusOnEditor();
  }

  private async _collectFeatureValues(): Promise<void> {
    const [bcSettings, hasNanorepFeature] = await Promise.all([
      this._bcSettingsService.getSettings(),
      this._nanorepService.hasNanorepFeature()
    ]);
    this.hasSmartResponseFeature = this._bcSettingsService.ChatFeatures.isEnabled(
      bcSettings,
      BcFeatures.SMART_RESPONSE
    );
    this.hasDiscussionsFeature =
      this._bcSettingsService.ChatFeatures.isEnabled(bcSettings, BcFeatures.LC_DISCUSSIONS) &&
      this._isDiscussionsEnabled() &&
      this._hasDiscussionPermission();
    this.isNanorepEnabled = hasNanorepFeature;
  }

  private _getChatWindowSettings(): void {
    if (this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_SEND_FILES_TO_VISITOR)) {
      this._bcRestApi.getChatWindowSettings(this.chat.ChatWindowDefID).then((chatWindowSettings) => {
        this.isFileTransferForOperatorEnabledSubject.next(
          chatWindowSettings &&
            chatWindowSettings.fileTransferForOperatorEnabled &&
            this._bcConfig &&
            this._bcConfig.current.features.fileShare
        );
      });
    }
  }

  isChatInProgress(): boolean {
    return !this.isAnswerable() && this.isChatActive() && !this.isWrapupPanelActive;
  }

  toggleMoreActions(): void {
    // Before opening the panel, update the flag that indicates that the messages list is scrolled to the bottom.
    this.setIsAttachedToBottom();

    this.isMoreActionsVisible = !this.isMoreActionsVisible;

    setTimeout(() => {
      if (!this.isMoreActionsVisible) {
        this.setFocusOnEditor();
      }
    });

    // Wait for the open animation to complete, and then scroll the messages list to the bottom, if necessary.
    setTimeout(() => {
      this.scrollToBottom();
    }, 1000);
  }

  isExpandedMode(): boolean {
    return this._workAreaManager.isExpandedMode() || !this.workItem.isCollapsible;
  }

  setFocusOnEditor(): void {
    if (this.editorInstance) {
      this._rxEmitter.send(broadcastEvents.focusEditor, null);
    } else {
      this._element.nativeElement.focus();
    }
  }

  onWrapUpChange(wrapUpChanges: WrapUp[][]): void {
    this.wrapups = wrapUpChanges;
  }

  loadWrapups(): Promise<void> {
    return this._wrapupPanelService.getWrapUps(this.workItem.type, this.chat.ChatID).then((wrapUps) => {
      this.wrapups = wrapUps;
      this.areWrapupsLoaded = true;
      if (this.wrapups) {
        this.editorCommandList.push({ shortcut: slashCommands.WRAP_UP, description: 'bcChatBox.commands.wrapup' });
      }
    });
  }

  hasWrapUps(): boolean {
    return !!this.wrapups;
  }

  isSmartResponseEnabled(): boolean {
    return this.hasSmartResponseFeature && (!this.isNanorepEnabled || !this.workItem.NanorepConfigID);
  }

  canAgentProposeArticle(): boolean {
    return (
      !!this.workItem.NanorepConfigID &&
      this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_SUGGEST_ARTICLE)
    );
  }

  areWrapupsLoading(): any {
    return !this.areWrapupsLoaded && (this.isWrapupPanelActive || this.isChatEnded());
  }

  haveWrapupsLoaded(): any {
    return this.areWrapupsLoaded && (this.isWrapupPanelActive || this.isChatEnded());
  }

  onJoinStateChange(): void {
    this.isAttached = true;
    this.scrollToBottom();
  }

  showBlockConfirmation(): void {
    this.blockConfirmationVisible = true;
  }

  closeBlockConfirmation(): void {
    this.blockConfirmationVisible = false;
  }

  setVideoConfirmationVisibility(isVisible: boolean): void {
    this.videoConfirmationModalVisible = isVisible;
  }

  setVideoAutoStartConfirmationVisibility(isVisible: boolean): void {
    this.videoAutoStartConfirmationModalVisible = isVisible;
  }

  setVideoWithAcdConfirmationVisibility(isVisible: boolean): void {
    this.videoWithAcdConfirmationModalVisible = isVisible;
  }

  setCancelRemoteControlSessionConfirmationVisibility(isVisible: boolean): void {
    this.cancelRemoteControlSessionConfirmationModalVisible = isVisible;
  }

  autoStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.AutoStartVideoCall}${this.workItem.id}`, null);
  }

  enforceAutoStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.EnforceAutoStartVideoCall}${this.workItem.id}`, null);
  }

  enforceStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.EnforceStartVideoCall}${this.workItem.id}`, null);
  }

  cancelStartVideoCall(): void {
    this._rxEmitter.send(`${MessageBusEvents.CancelStartVideoCall}${this.workItem.id}`, null);
  }

  ignoreAutoStartVideo(): void {
    this._rxEmitter.send(`${MessageBusEvents.IgnoreAutoStartVideoCall}${this.workItem.id}`, null);
  }

  async cancelRemoteControlSession(): Promise<void> {
    this.setCancelRemoteControlSessionConfirmationVisibility(false);
    await this._remoteControlService.cancelRemoteControlSession(this.workItem.id);
  }

  continueRemoteControlSession(): void {
    this.setCancelRemoteControlSessionConfirmationVisibility(false);
  }

  async blockChat(): Promise<void> {
    this.closeBlockConfirmation();
    await this._chats.blockChat(this.chat.ChatID);
    await this.endChat();
  }

  private _isDiscussionsEnabled(): boolean {
    return this._discussionsService.isDiscussionsEnabled();
  }

  private _hasDiscussionPermission(): boolean {
    return this._discussionsService.hasDiscussionPermission(this.workItem.type);
  }

  private _hasBlockChatPermission(): boolean {
    return this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_BLOCK_IP);
  }

  private _hasFlagPermission(): boolean {
    return (
      this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_FLAG) ||
      this._userPermissionService.hasPermissionFor(ActionPermissionType.CHAT_UNFLAG)
    );
  }
}
